import { useCallback, useState } from 'react';
import { ActivityIndicator, Pressable, TextInput } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';

// --- Styled Components ---

const Container = styled.View`
  padding: 16px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 15px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 4px;
`;

const Description = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 12px;
`;

const InputWrapper = styled.View`
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 12px;
`;

const ActionRow = styled.View`
  flex-direction: row;
  justify-content: flex-end;
`;

const SubmitButton = styled(Pressable)`
  background-color: ${REDESIGN_COLORS.ctaBlue};
  padding: 10px 20px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const SubmitButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

/**
 * NeedsInputPrompt - Shown when the latest draft has strategy_archetype
 * 'escalate_to_buyer', meaning the dealer asked a question that needs
 * the user's input before a new draft can be generated.
 *
 * @param {Object} props
 * @param {Function} props.onSubmit - Called with the user's text input
 */
const NeedsInputPrompt = ({ onSubmit }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return;

    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(text.trim());
      }
      setText('');
    } finally {
      setSubmitting(false);
    }
  }, [text, onSubmit]);

  return (
    <Container>
      <Title>Your input needed</Title>
      <Description>
        The dealer has asked something that only you can answer.
        Please provide the information below so we can draft a response.
      </Description>

      <InputWrapper>
        <TextInput
          style={{
            fontFamily: 'Satoshi-Regular',
            fontSize: 14,
            color: REDESIGN_COLORS.textPrimary,
            padding: 12,
            minHeight: 80,
            textAlignVertical: 'top',
            outlineStyle: 'none',
          }}
          value={text}
          onChangeText={setText}
          placeholder="Type your answer here..."
          placeholderTextColor={REDESIGN_COLORS.textSecondary}
          multiline
          numberOfLines={3}
          editable={!submitting}
        />
      </InputWrapper>

      <ActionRow>
        <SubmitButton onPress={handleSubmit} disabled={!text.trim() || submitting}>
          {submitting ? (
            <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
          ) : (
            <SubmitButtonText>Submit</SubmitButtonText>
          )}
        </SubmitButton>
      </ActionRow>
    </Container>
  );
};

export default NeedsInputPrompt;
